<template>
  <nav class="pagination-container" v-if="pageList.length > 1" aria-label="Episode pages">
    <!-- 🔴 D-233 (A8b): these were an hrefless `<a type="button">` and an `<li @click>` —
         mouse-only, all three of them. `type` is inert on an `<a>`, which is the clearest
         possible sign that somebody already knew these were buttons.

         🪤 AND THE HANDLER DISPATCHES ON `event.target`, WHICH IS WHY THIS IS NOT A TAG
         SWAP. `switchPage` reads `event.target.innerText` to tell Previous from Next from
         a page number, and `event.target.getAttribute('page')` for the number itself. So
         the text and the `page` attribute must both land on THE ELEMENT THAT RECEIVES THE
         CLICK. Wrapping the number in a `<span>` inside the button, or leaving `page` on
         the `<li>`, moves `event.target` and `parseInt(null)` makes `pageNumber` -1 —
         paging silently stops working, with no error. Measured by planting exactly that.

         The handler is untouched: it is correct, and a keyboard Enter on a `<button>`
         fires a click whose target IS the button, so it now works from a keyboard for the
         first time without a line of it changing. -->
    <button class="active pagination-item traverse-item" type="button" v-on:click="switchPage">Previous</button>
    <ul class="pagination">
      <li v-bind:class="`pagination-item ${page.classList}`" v-for="page in pageList"
        v-bind:key="page.pageNumber"
      >
        <!-- D-271: the "..." separator was rendered as a focusable <button> whose click
             is a no-op (switchPage matches '...' -> pageNumber -1 -> early return), so it
             was a dead keyboard tab stop. Render it as a decorative aria-hidden <span>;
             only real page numbers stay buttons. -->
        <span v-if="page.classList.includes('concat-item')" aria-hidden="true">{{ page.pageNumber }}</span>
        <button
          v-else
          type="button"
          class="pagination-page"
          v-bind:page="page.pageNumber"
          v-bind:aria-current="page.classList.includes('active') ? 'page' : null"
          v-on:click="switchPage"
        >{{ page.pageNumber }}</button>
      </li>
    </ul>
    <button class="active pagination-item traverse-item" type="button" v-on:click="switchPage">Next</button>
  </nav>
</template>

<script>
export default {
  components: {},
  props: [
    'category',
    'pageList',
  ],
  methods: {
    switchPage(event) {
      event.preventDefault();

      const pageText = event.target.innerText;
      const pageTotal = this.pageList[this.pageList.length - 1].pageNumber;

      let pageNumber = -1;

      // Check to see if we have a page seperator
      const activePage = this.pageList.find((page) => (
        page.classList.includes('active')
      ));
      const currentPage = activePage ? activePage.pageNumber : 1;

      // Handle our previous, next, seperator, and regular links
      if (pageText === 'Previous') {
        if (currentPage !== 1) {
          pageNumber = currentPage - 1;
        } else {
          pageNumber = -1;
        }
      } else if (pageText === 'Next') {
        if (currentPage !== pageTotal) {
          pageNumber = currentPage + 1;
        } else {
          pageNumber = -1;
        }
      } else if (pageText === '...') {
        pageNumber = -1;
      } else {
        pageNumber = parseInt(event.target.getAttribute('page') || '-1', 10);
      }

      // forward to parent only if our next page is greater than 0
      if (pageNumber > 0) {
        this.$parent.switchPage(pageNumber, this.category);
      }
    },
  },
};
</script>

<style lang="less" scoped>
  // D-233 (A8b): the UA chrome comes off in the same commit as the tag change, or the
  // conversion IS a look change.
  //
  // 🔴 AND THE FIRST VERSION OF THIS RESET WAS ITSELF THE LOOK CHANGE. It was written as
  // `.pagination-container button { border: 0; font: inherit; … }`, which at specificity
  // (0,1,1) OUTSPECIFIES `.pagination-item` at (0,1,0) — and the two arrows carry
  // `.pagination-item` themselves. So the reset stripped their `border: 1px solid gray`
  // and their `font-size: 14px` and handed them the surrounding font instead. A blanket
  // "take the UA chrome off every button" rule is only safe where no button is also
  // styled by something weaker.
  //
  // 🔑 THE TWO ARROWS NEED NO RESET AT ALL. `.pagination-item` already sets
  // `appearance: none`, `background-color: transparent`, `border`, `color`, `font-family`,
  // `font-size` and `padding` — author declarations, so they beat the UA sheet on every
  // property a `<button>` would otherwise bring. The reset belongs ONLY on the page-number
  // button, which sits INSIDE an `<li>` that carries `.pagination-item`.
  //
  // 🔑 And note `.pagination-item > button { color: gray }` further down: it predates this
  // MR. The stylesheet was already written for a button inside the list item, which is a
  // fair sign these were meant to be buttons before somebody reached for `<li @click>`.
  .pagination-page {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    margin: 0;
    font: inherit;
    line-height: inherit;
    color: inherit;
    cursor: pointer;
    // No `display` override: the `<li>`'s content was a TEXT NODE, so its line box came
    // from the strut. An inline-block button carrying the same text and the same
    // line-height produces the same box; forcing `display: block` replaces the inline
    // formatting context and moves the baseline.
    vertical-align: baseline;
  }

  // D-267 (A8 follow-up): the page-number click target must FILL the bordered box, not
  // shrink to the digit. The `<li>` carried `padding: 5px 10px` (its border draws the
  // ~28x24 visible box) while the button carried `padding: 0`, so the click landed only on
  // the ~14px glyph and the surrounding padding was dead — WCAG 2.5.5 / 2.5.8. Move the
  // padding onto the button, which IS the click target, and zero it on the `<li>`, so box
  // and target coincide. The border stays on the li and the digit keeps its place, so the
  // look does not move. Scoped to the real page numbers: the `concat-item` separator keeps
  // the base `padding: 0` from `.pagination-page` above (it is a non-interactive '...').
  .pagination > li.pagination-item:not(.concat-item) {
    padding: 0;

    & > .pagination-page {
      display: block;
      padding: 5px 10px;
    }
  }

  .dark-theme {
    & .pagination-item {
      background-color: transparent;
      border: 1px solid white;
      color: white;

      &:hover,
      &.active {
        background-color: white;
        color: gray;
      }

      &.concat-item {
        color: white;
      }
    }
  }

  .pagination {
    display: flex;
    padding: 0;

    & > li {
      list-style-type: none;
    }
  }

  .pagination-container {
    align-items: center;
    display: flex;
    justify-content: center;
  }

  .pagination-item {
    appearance: none;
    background-color: transparent;
    border: 1px solid gray;
    color: gray;
    font-family: 'Helvetica Neue',Helvetica,Arial,sans-serif;
    font-size: 14px;
    margin-right: 5px;
    padding: 5px 10px;
    text-decoration: none;

    &:hover {
      cursor: pointer;
    }

    &:hover,
    &.active {
      background-color: gray;
      color: white;

      & > button {
        color: white;
      }
    }

    &.concat-item {
      align-self: flex-end;
      background-color: transparent;
      border: none;
      padding: 0;

      &:hover {
        background-color: transparent;
        color: gray;
        cursor: default;
      }
    }

    & > button {
      color: gray;
      text-decoration: none;

      &:hover {
        text-decoration: none;
      }
    }
  }

  .traverse-item {
    font-weight: bold;
  }
</style>
